import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, memo } from 'react';
import { BoilerAdapter, get_z3k_object_state, HeatingCircuit, RelayControl, } from 'z3k-config';
import { ResponsiveIcon } from 'console/shared/components/icon/responsive';
import Relay from './icons/relay.svg';
import RelayOff from './icons/relay-off.svg';
import RelayOn from './icons/relay-on.svg';
import Flame from './icons/control-flame.svg';
import ProFlame from './icons/pro-control-flame.svg';
import { useZ3KDeviceState } from 'console/shared/hooks/device/z3k';
import { AnimatedFlame } from 'console/shared/components/features/animated-icons/flame';
import { useAllowAnimatedFlame } from 'console/shared/hooks/helpers';
/**
 * @function
 * Надо показать по какой логике управляется котёл
 * 1. через АЦШ:
 *          показываем красный огонёк, когда котёл работает, иначе серый огонёк (факт)
 * 2. через реле:
 *          реле замкнуто или разомкнуто (предположение о работе котла)
 * @param hc - котёл
 * @param z3k_config - конфигурация
 * @param z3k_state - состояние
 * @return Возвращает режим управления и подсказку
 */
export function boilerControl(hc, z3k_config, z3k_state) {
    const relay = RelayControl.get.one(z3k_config, ({ id }) => hc.control_devices.includes(id));
    if (relay) {
        const relay_state = get_z3k_object_state(z3k_state, relay.id);
        if (relay_state) {
            const { active } = relay_state;
            return {
                control_mode: active ? 'relay-on' : 'relay-off',
                title: active ? 'Замкнуто' : 'Разомкнуто',
            };
        }
        return { control_mode: 'relay', title: 'Неизвестно' };
    }
    const boiler_adapter = BoilerAdapter.get.one(z3k_config, ({ id }) => hc.control_devices.includes(id));
    if (boiler_adapter) {
        const hc_state = get_z3k_object_state(z3k_state, hc.id);
        if (hc_state != null && HeatingCircuit.isWorking(hc_state)) {
            return { control_mode: 'ot-heat', title: 'Котёл активен' };
        }
        return { control_mode: 'ot', title: 'Котёл не активен' };
    }
    return { control_mode: null, title: null };
}
const IconElement = memo(forwardRef((props, ref) => {
    const { control_mode, anim_icon, ...rest } = props;
    switch (control_mode) {
        case 'ot':
            return anim_icon ? (_jsx(ResponsiveIcon, { Icon: ProFlame, ...rest, ref: ref, large: true })) : (_jsx(ResponsiveIcon, { Icon: Flame, ...rest, ref: ref }));
        case 'ot-heat':
            return anim_icon ? (_jsx(AnimatedFlame, { ...rest })) : (_jsx(ResponsiveIcon, { Icon: Flame, ref: ref, ...rest, color: rest.color === 'inherit' ? 'warning' : 'error' }));
        case 'relay':
            return _jsx(ResponsiveIcon, { Icon: Relay, ...rest, ref: ref });
        case 'relay-on':
            return (_jsx(ResponsiveIcon, { Icon: RelayOn, ref: ref, color: "error", ...rest }));
        case 'relay-off':
            return _jsx(ResponsiveIcon, { Icon: RelayOff, ...rest, ref: ref });
        default:
            return null;
    }
}));
IconElement.displayName = 'IconElement';
export function BoilerControlIcon(props) {
    const { hc, z3k_config, color, ...rest } = props;
    const z3k_state = useZ3KDeviceState();
    const { control_mode, title } = boilerControl(hc, z3k_config, z3k_state);
    const animated_icon = useAllowAnimatedFlame();
    if (!control_mode || !title) {
        return null;
    }
    return (_jsx(IconElement, { control_mode: control_mode, color: color, anim_icon: animated_icon, ...rest }));
}
